import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { formatPeriod } from '../../kit/helpers'
import { METRIC_NAMES, controlRoomUsageText, ptsUsageText } from './constants'

// Busy and error state for one action dialog. `run(body)` hands the body to
// the panel's `onSubmit` (which posts it and resolves { ok, error }), closes
// the dialog on success and otherwise shows the server's message.
export const useActionSubmit = ({ open, onSubmit, onClose }) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setBusy(false)
      setError('')
    }
  }, [open])

  const run = async (body) => {
    setBusy(true)
    setError('')
    const result = await onSubmit(body)
    setBusy(false)
    if (result?.ok) onClose()
    else setError(result?.error || 'That did not save.')
  }

  return { busy, error, run }
}

// "PTS journeys 612 of 500 · Control-room hours 231.5 of 200.0" for the
// metrics a month exceeded (both when none is listed). Figures are the
// record's own; a target without figures only names the metrics.
export const usageParts = (target) => {
  const exceeded = target?.exceededMetrics ?? []
  const wanted = exceeded.length ? exceeded : ['ptsJourneysPerMonth', 'controlRoomHoursPerMonth']
  return wanted
    .map((metric) => {
      if (metric === 'ptsJourneysPerMonth') {
        return target?.pts ? `${METRIC_NAMES[metric]} ${ptsUsageText(target)}` : METRIC_NAMES[metric]
      }
      if (metric === 'controlRoomHoursPerMonth') {
        return target?.controlRoom
          ? `${METRIC_NAMES[metric]} ${controlRoomUsageText(target)}`
          : METRIC_NAMES[metric]
      }
      return METRIC_NAMES[metric] ?? metric
    })
    .filter(Boolean)
}

// The month an action dialog is about: period, occurrence and the usage the
// month was finalised on.
export const RecordSummary = ({ target }) => (
  <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: '#F8FAFC', px: 1.5, py: 1 }}>
    <Typography variant="body2" fontWeight={700}>
      {formatPeriod(target?.period, 'MMMM YYYY')}
      {target?.occurrenceNumber ? ` · occurrence ${target.occurrenceNumber}` : ''}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block">
      {usageParts(target).join(' · ')}
    </Typography>
  </Box>
)

// Shell shared by the action dialogs: title, body, the server's error message,
// Cancel and a confirm button that reads "Saving…" while the request runs.
const ActionDialog = ({
  open,
  title,
  children,
  confirmLabel,
  confirmColor = 'primary',
  confirmDisabled = false,
  busy = false,
  error,
  maxWidth = 'sm',
  onConfirm,
  onClose,
}) => (
  <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth={maxWidth} fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Stack gap={2} paddingTop={1}>
        {children}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color={confirmColor}
        onClick={onConfirm}
        disabled={busy || confirmDisabled}
      >
        {busy ? 'Saving…' : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
)

export default ActionDialog
